// Angular Imports
import { Injectable } from '@angular/core'
import { environment } from 'src/environments/environment.prod'

declare global {
	interface Window {
		gtag?: (...args: any[]) => void
		dataLayer?: any[]
	}
}

@Injectable({
	providedIn: 'root',
})
export class GoogleAnalyticsService {
	// 🔧 DEBUG MODE: Set to true to test GA in development
	// ⚠️ Remember to set back to false when not testing
	private readonly DEBUG_MODE = true

	constructor() {
		// Enable GA4 DebugView mode
		if (this.DEBUG_MODE && typeof window !== 'undefined' && window.gtag) {
			window.gtag('set', 'debug_mode', true)
			// console.log('[GA] DebugView mode enabled')
		}
	}

	/**
	 * Check if tracking should be enabled
	 * Tracking is enabled if: production OR debug mode is active
	 */
	private shouldTrack(): boolean {
		return environment.production || this.DEBUG_MODE
	}

	/**
	 * Send a custom event to Google Analytics
	 * @param eventName The name of the event (e.g., 'button_click', 'form_submit')
	 * @param eventCategory Category of the event (e.g., 'engagement', 'navigation')
	 * @param eventLabel Optional label for the event
	 * @param eventValue Optional numeric value for the event
	 * @param customParams Optional additional custom parameters
	 */
	trackEvent(
		eventName: string,
		eventCategory: string,
		eventLabel?: string,
		eventValue?: number,
		customParams?: Record<string, any>
	): void {
		const eventParams: any = {
			event_category: eventCategory,
			...(eventLabel && { event_label: eventLabel }),
			...(eventValue && { value: eventValue }),
			...customParams,
		}

		if (!this.shouldTrack()) {
			// console.log('[GA - DEV MODE] Event would be tracked:', eventName, eventParams)
			return
		}

		if (typeof window !== 'undefined' && window.gtag) {
			window.gtag('event', eventName, eventParams)
			// console.log(
			// 	`[GA - ${this.DEBUG_MODE ? 'DEBUG' : 'PROD'}] Event tracked:`,
			// 	eventName,
			// 	eventParams
			// )
		} else {
			//console.warn('Google Analytics is not initialized')
		}
	}

	/**
	 * Track a page view
	 * @param pageTitle The title of the page
	 * @param pagePath The path of the page
	 */
	trackPageView(pageTitle: string, pagePath: string): void {
		if (!this.shouldTrack()) {
			// console.log('[GA - DEV MODE] Page view would be tracked:', pageTitle, pagePath)
			return
		}

		if (typeof window !== 'undefined' && window.gtag) {
			window.gtag('event', 'page_view', {
				page_title: pageTitle,
				page_path: pagePath,
			})
			// console.log(`[GA - ${this.DEBUG_MODE ? 'DEBUG' : 'PROD'}] Page view tracked:`, pagePath)
		}
	}

	/**
	 * Set user properties
	 * @param properties Object with user properties
	 */
	setUserProperties(properties: Record<string, any>): void {
		if (!this.shouldTrack()) {
			// console.log('[GA - DEV MODE] User properties would be set:', properties)
			return
		}

		if (typeof window !== 'undefined' && window.gtag) {
			window.gtag('set', 'user_properties', properties)
			// console.log(`[GA - ${this.DEBUG_MODE ? 'DEBUG' : 'PROD'}] User properties set`)
		}
	}
}
